import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Globe,
  Clock,
  CheckCircle,
  PlusCircle,
  RefreshCw,
  AlertCircle,
  ChevronLeft,
  Filter,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending:  'bg-red-500/20 text-red-400 border border-red-500/30',
  reviewed: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
  added:    'bg-green-500/20 text-green-400 border border-green-500/30',
};

const STATUS_LABELS = {
  pending:  'Pending',
  reviewed: 'Reviewed',
  added:    'Added',
};

export default function AdminLanguageRequests() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const fetchRequests = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setRefreshing(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await axios.get(
        `${API_URL}/api/language-requests/admin${params}`,
        { withCredentials: true }
      );
      setRequests(res.data.requests || []);
      setTotal(res.data.total || 0);
      setPendingCount(res.data.pending_count || 0);
    } catch (err) {
      console.error('Failed to load language requests:', err);
      toast.error('Failed to load language requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const updateStatus = async (requestId, newStatus) => {
    setUpdatingId(requestId);
    try {
      await axios.patch(
        `${API_URL}/api/language-requests/admin/${requestId}?status=${newStatus}`,
        null,
        { withCredentials: true }
      );
      toast.success(`Marked as ${STATUS_LABELS[newStatus]}`);
      fetchRequests(true);
    } catch (err) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center gap-1 text-slate-400 hover:text-white transition"
          >
            <ChevronLeft className="w-4 h-4" />
            Admin
          </button>
          <span className="text-slate-600">/</span>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Globe className="w-6 h-6 text-red-400" />
            Language Requests
            {pendingCount > 0 && (
              <span
                data-testid="pending-badge"
                className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white"
              >
                {pendingCount} pending
              </span>
            )}
          </h1>
          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={() => fetchRequests()}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg text-sm transition disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex items-center gap-3 mb-6">
          <Filter className="w-4 h-4 text-slate-400" />
          <span className="text-slate-400 text-sm">Filter:</span>
          {['', 'pending', 'reviewed', 'added'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`filter-${s || 'all'}`}
              className={`px-3 py-1 rounded-full text-sm transition ${
                statusFilter === s
                  ? 'bg-red-600 text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {s ? STATUS_LABELS[s] : 'All'}
            </button>
          ))}
          <span className="ml-auto text-slate-500 text-sm">{total} total</span>
        </div>

        {/* Empty state */}
        {requests.length === 0 && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-12 text-center">
            <Globe className="w-12 h-12 text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 text-lg font-medium">No language requests yet</p>
            <p className="text-slate-600 text-sm mt-1">
              Requests submitted from the onboarding page will appear here.
            </p>
          </div>
        )}

        {/* Request list */}
        <div className="space-y-3">
          {requests.map(req => (
            <div
              key={req.request_id}
              data-testid={`request-card-${req.request_id}`}
              className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
            >
              {/* Language name */}
              <div className="flex-1 min-w-0">
                <p
                  data-testid="request-language-name"
                  className="text-white font-semibold text-lg truncate"
                >
                  {req.language_name}
                </p>
                <div className="flex flex-wrap items-center gap-3 mt-1">
                  <span className="text-slate-400 text-sm truncate">
                    {req.user_name || req.user_email || req.user_id}
                  </span>
                  <span className="flex items-center gap-1 text-slate-500 text-xs">
                    <Clock className="w-3 h-3" />
                    {formatDate(req.created_at)}
                  </span>
                  {req.reviewed_at && (
                    <span className="text-slate-600 text-xs">
                      Reviewed {formatDate(req.reviewed_at)}
                      {req.reviewed_by ? ` by ${req.reviewed_by}` : ''}
                    </span>
                  )}
                </div>
                {req.note && (
                  <p className="text-slate-500 text-sm mt-2 italic">Note: {req.note}</p>
                )}
              </div>

              {/* Status + actions */}
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[req.status]}`}>
                  {STATUS_LABELS[req.status]}
                </span>

                {req.status === 'pending' && (
                  <>
                    <button
                      onClick={() => updateStatus(req.request_id, 'reviewed')}
                      disabled={updatingId === req.request_id}
                      data-testid={`mark-reviewed-${req.request_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-amber-600/20 hover:bg-amber-600/40 text-amber-400 rounded-lg text-xs transition disabled:opacity-50"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Mark Reviewed
                    </button>
                    <button
                      onClick={() => updateStatus(req.request_id, 'added')}
                      disabled={updatingId === req.request_id}
                      data-testid={`mark-added-${req.request_id}`}
                      className="flex items-center gap-1 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/40 text-green-400 rounded-lg text-xs transition disabled:opacity-50"
                    >
                      <PlusCircle className="w-3.5 h-3.5" />
                      Mark Added
                    </button>
                  </>
                )}

                {req.status === 'reviewed' && (
                  <button
                    onClick={() => updateStatus(req.request_id, 'added')}
                    disabled={updatingId === req.request_id}
                    data-testid={`mark-added-${req.request_id}`}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/40 text-green-400 rounded-lg text-xs transition disabled:opacity-50"
                  >
                    <PlusCircle className="w-3.5 h-3.5" />
                    Mark Added
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
